import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { getMyLevels, acknowledgeLevel } from '../api/levels';
import { getLevelDef, MAX_LEVEL } from '../constants/levels';
import { useTranslation } from '../i18n';
import analytics from '../lib/analytics';

/**
 * Global celebration moment — mounted once in AppShell (like
 * ConfirmAutoReleaseDialog) so a level-up discovered anywhere in the app
 * surfaces here, not just on a specific page. Shows once per level
 * (pendingCelebration comes from the server, cleared via acknowledgeLevel
 * on dismiss) and never fires for backfilled levels — see
 * levelService.recordLevel's is_backfilled handling.
 */
export function LevelUpModal() {
  const qc = useQueryClient();
  const { t } = useTranslation();

  const { data } = useQuery({
    queryKey: ['levels'],
    queryFn:  () => getMyLevels().then((r) => r.data.data),
    staleTime: 60_000,
  });

  const ackMutation = useMutation({
    mutationFn: (level: number) => acknowledgeLevel(level),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['levels'] }),
  });

  const level = data?.pendingCelebration;
  if (!level) return null;

  const def = getLevelDef(level);
  if (!def) return null;

  const nextDef = level < MAX_LEVEL ? getLevelDef(level + 1) : undefined;

  function dismiss() {
    analytics.trackLevelUpAcknowledged(level!);
    ackMutation.mutate(level!);
  }

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(26,28,30,0.55)', zIndex: 400 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 'calc(100% - 48px)', maxWidth: '320px', zIndex: 401,
        background: '#fff', borderRadius: '24px', padding: '32px 28px 28px', textAlign: 'center',
        boxShadow: '0 20px 48px -8px rgba(0,0,0,0.3)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '18px' }}>
          <div style={{
            width: '88px', height: '88px', borderRadius: '9999px',
            background: 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(0,91,192,0.35)',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: '44px', color: '#f7f7ff' }}>
              {def.icon}
            </span>
          </div>
        </div>

        <div style={{
          display: 'inline-block', background: 'var(--primary-container)', color: 'var(--primary)',
          fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
          padding: '4px 14px', borderRadius: '9999px', marginBottom: '12px',
        }}>
          {t('level_word')} {level}
        </div>

        <div style={{ fontFamily: 'var(--manrope)', fontWeight: 800, fontSize: '24px', color: 'var(--on-surface)', marginBottom: '8px' }}>
          {t(def.titleKey)}
        </div>

        <div style={{ fontSize: '14px', color: 'var(--on-surface-3)', lineHeight: 1.6, marginBottom: '20px' }}>
          {t(def.descKey)}
        </div>

        <button
          onClick={dismiss}
          disabled={ackMutation.isPending}
          style={{
            width: '100%', padding: '14px', borderRadius: '12px', border: 'none',
            background: 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
            color: '#f7f7ff', fontFamily: 'var(--manrope)', fontSize: '15px', fontWeight: 700,
            cursor: ackMutation.isPending ? 'default' : 'pointer',
            opacity: ackMutation.isPending ? 0.7 : 1,
            marginBottom: nextDef ? '12px' : 0,
          }}
        >
          {t('level_up_modal_cta')}
        </button>

        {nextDef && (
          <div style={{ fontSize: '12px', color: 'var(--on-surface-4, #9ca3af)' }}>
            {t('level_up_modal_next').replace('{title}', t(nextDef.titleKey))}
          </div>
        )}
      </div>
    </>
  );
}
